import { Injectable } from '@angular/core';
import { BehaviorSubject, timer } from 'rxjs';
import { TextSizeService } from './text-size.service';

interface CustomWindow extends Window {
  isOpenedInWebView?: boolean;
  userTheme?: 'light' | 'dark';
  userFontSize?: 'small' | 'medium' | 'large';
}

// Make custom window properties accessible.
declare let window: CustomWindow;

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private _theme = new BehaviorSubject<'light' | 'dark'>('light');
  theme$ = this._theme.asObservable();

  private readonly localStorageKeys = {
    theme: 'app.theme',
  };

  constructor(private textSizeService: TextSizeService) {
    // Load saved settings from local storage.
    if (localStorage.getItem(this.localStorageKeys.theme) !== null) {
      this.setTheme(
        JSON.parse(localStorage.getItem(this.localStorageKeys.theme) || ''),
      );
    }

    // Set theme when changed.
    this.theme$.subscribe((theme) => {
      document.getElementsByTagName('html')[0].className =
        `${theme}-theme font-${this.textSizeService.getTextSize()}`;
    });

    // Set base font size. This is set here to avoid a circular dependency with TextSizeService.
    this.textSizeService.textSize$.subscribe((size) => {
      document.getElementsByTagName('html')[0].className =
        `${this.getTheme()}-theme font-${size}px`;
    });
  }

  /** Setup a timed monitor of window.userTheme to detect theme changes sent from mobile app. */
  initThemeWatcher(): void {
    timer(0, 500).subscribe(() => {
      if (window?.userTheme) {
        if (window.userTheme === 'light' && this._theme.getValue() === 'dark') {
          this.setTheme('light');
        } else if (
          window.userTheme === 'dark' &&
          this._theme.getValue() === 'light'
        ) {
          this.setTheme('dark');
        }
      }
    });
  }

  toggleTheme(): void {
    if (this._theme.getValue() === 'light') {
      this.setTheme('dark');
    } else {
      this.setTheme('light');
    }
  }

  getTheme(): 'light' | 'dark' {
    return this._theme.getValue();
  }

  setTheme(newTheme: 'light' | 'dark'): void {
    this._theme.next(newTheme);
    localStorage.setItem(this.localStorageKeys.theme, JSON.stringify(newTheme));
  }
}
